import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { daysText } from '../availability/conflicts';
import { packlistUrl } from '../subrentals/elsewhere';
import type { ShortageHolder } from './types';

/** How long the pointer must rest on „brakuje N” before the holders show (keyboard focus and a click: at once). */
export const HOLDERS_DELAY_MS = 300;
/** Leaving for the bubble (a gap of a few pixels) does not close it. */
const HIDE_DELAY_MS = 200;

/** „Wesele A (7.11.2026 – 8.11.2026) — 2 szt.” without the name: the days and the pieces. */
export const holderRest = (h: Pick<ShortageHolder, 'firstDay' | 'lastDay' | 'quantity'>): string => ` (${daysText(h.firstDay, h.lastDay)}) — ${h.quantity} szt.`;

/**
 * Owner 2026-10-25 00:39: „jak najadę na »brakuje N« to napisz do jakiego koncertu jest przypisane i daj link do
 * sprzętówki”. On hover / keyboard focus / a click of „brakuje N”: a bubble with who holds the item over the line's days
 * — those ahead in the queue, taking its pieces. An event's name links to its packlist;
 * a hidden event („Inne wydarzenie”) and a block („Blokada: <powód>” / „Inne zajęcie”) have no link (rule 7a: the
 * server names them). Esc, leaving or blur closes it. Without holders: only the text.
 */
export function ShortageHolders({ holders, children }: { holders: readonly ShortageHolder[] | undefined; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);
  if (!holders || holders.length === 0) return <>{children}</>;

  const later = (value: boolean, ms: number) => {
    clear();
    timer.current = setTimeout(() => setOpen(value), ms);
  };
  const now = (value: boolean) => {
    clear();
    setOpen(value);
  };
  const row = (h: ShortageHolder, i: number) => (
    <span key={`${h.eventId ?? 'x'}-${i}`} className="q-short-holder">
      {h.eventId ? (
        <Link to={packlistUrl(h.eventId)} title="Otwórz sprzętówkę tego wydarzenia">
          {h.name}
        </Link>
      ) : (
        h.name
      )}
      {holderRest(h)}
    </span>
  );
  return (
    <span
      className="q-short-who"
      onMouseEnter={() => later(true, HOLDERS_DELAY_MS)}
      onMouseLeave={(e) => {
        if (!(document.activeElement instanceof Node && e.currentTarget.contains(document.activeElement))) later(false, HIDE_DELAY_MS);
        else clear();
      }}
      onFocus={() => now(true)}
      onBlur={(e) => {
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) now(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          now(false);
        }
      }}
    >
      <span className="q-short-text" tabIndex={0} aria-describedby={open ? id : undefined} onClick={() => now(true)}>
        {children}
      </span>
      {open && (
        // Safari (#110): a click on a link does not focus it — the text keeps the focus, the bubble stays to the click
        <span id={id} role="tooltip" className="q-short-pop" onMouseDown={(e) => e.preventDefault()}>
          <span className="q-short-pop-head">Zajęte w:</span>
          {holders.map(row)}
        </span>
      )}
    </span>
  );
}
